import {
  HttpClientContext,
  HttpProjectSetupContext,
  HttpUserAdminContext,
  HttpProjectAdminContext,
  HttpDprContext,
  HttpSharedContext,
  HttpSharedCoreContext,
  HttpSharedFileContext,
  HttpDprDetailsContext,
  HttpReportContext,
} from '@shared/enums';

const API_DPR = '/api/dpr';
const API_DPRAPPROVERS = '/api/dprapprovers';
const API_DIGITALSIGNATURE = '/api/digitalsignature';
const API_EXPORTDATA = '/api/exportdata';
const API_DPRDETAILS = '/api/dprdetails';
const API_PROJECTSETUP = '/api/projectsetup';
const API_SETTINGS = '/api/settings';
const API_USERSADMIN = '/api/usersadmin';
const API_REPORT = '/api/downloaddprreport';
const API_HEARTBEAT = '/api/heartbeat';
const API_USERSESSION = '/api/usersession';
const API_FILES = '/files';

const SYNCHRONIZE = '/synchronize';

export const routeMapping: {
  [key: string]: (args?: {
    [key: string]: string | number | boolean;
  }) => string;
} = {
  //#region Clients

  // Clients
  [HttpClientContext.GetClients]: () => {
    return `${API_SETTINGS}/clients`;
  },
  [HttpClientContext.SyncClients]: () => {
    return `${API_SETTINGS}${SYNCHRONIZE}/clients`;
  },

  //#endregion Clients

  //#region Project setup

  // Release subproject
  [HttpProjectSetupContext.ReleaseSubproject]: () => {
    return `${API_PROJECTSETUP}/release`;
  },

  // Configuration
  [HttpProjectSetupContext.GetConfiguration]: () => {
    return `${API_PROJECTSETUP}/configuration`;
  },
  [HttpProjectSetupContext.GetClientInfo]: () => {
    return `${API_PROJECTSETUP}/clientinfo`;
  },
  [HttpProjectSetupContext.SyncConfiguration]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/configuration`;
  },

  // Location categories
  [HttpProjectSetupContext.GetLocationCategories]: () => {
    return `${API_PROJECTSETUP}/locationcategories`;
  },
  [HttpProjectSetupContext.SyncLocationCategories]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/locationcategories`;
  },

  // Locations
  [HttpProjectSetupContext.GetLocations]: () => {
    return `${API_PROJECTSETUP}/locations`;
  },
  [HttpProjectSetupContext.SyncLocations]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/locations`;
  },

  // Custom-Data
  [HttpProjectSetupContext.GetCustomData]: () => {
    return `${API_PROJECTSETUP}/customdata`;
  },
  [HttpProjectSetupContext.SyncCustomData]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/customdata`;
  },

  // Activity streams
  [HttpProjectSetupContext.GetActivityStreams]: () => {
    return `${API_PROJECTSETUP}/activitystreams`;
  },
  [HttpProjectSetupContext.GetActivityStreamsWithTerminologies]: () => {
    return `${API_PROJECTSETUP}/activitystreamswithterminologies`;
  },
  [HttpProjectSetupContext.SyncActivityStreams]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/activitystreams`;
  },

  // Terminologies
  [HttpProjectSetupContext.GetTerminologies]: () => {
    return `${API_PROJECTSETUP}/terminologies`;
  },
  [HttpProjectSetupContext.GetSubprojectTerminologies]: () => {
    return `${API_PROJECTSETUP}/subprojectterminologies`;
  },

  // Activity groups
  [HttpProjectSetupContext.GetActivityGroups]: () => {
    return `${API_PROJECTSETUP}/activitygroups`;
  },
  [HttpProjectSetupContext.SyncActivityGroups]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/activitygroups`;
  },

  // Activities
  [HttpProjectSetupContext.GetActivities]: () => {
    return `${API_PROJECTSETUP}/activities`;
  },
  [HttpProjectSetupContext.SyncActivities]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/activities`;
  },

  // Status tracker
  [HttpProjectSetupContext.GetStatusTracker]: () => {
    return `${API_PROJECTSETUP}/statustracker`;
  },
  [HttpProjectSetupContext.SyncStatusTracker]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/statustracker`;
  },

  // Weather
  [HttpProjectSetupContext.GetWeather]: () => {
    return `${API_PROJECTSETUP}/weather`;
  },
  [HttpProjectSetupContext.SyncWeather]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/weather`;
  },

  // HSE
  [HttpProjectSetupContext.GetHse]: () => {
    return `${API_PROJECTSETUP}/hse`;
  },
  [HttpProjectSetupContext.SyncHse]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/hse`;
  },

  // Personnel
  [HttpProjectSetupContext.GetPersonnel]: () => {
    return `${API_PROJECTSETUP}/personnel`;
  },
  [HttpProjectSetupContext.SyncPersonnel]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/personnel`;
  },

  // Consumables
  [HttpProjectSetupContext.GetConsumables]: () => {
    return `${API_PROJECTSETUP}/consumables`;
  },
  [HttpProjectSetupContext.SyncConsumables]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/consumables`;
  },

  // Approvers
  [HttpProjectSetupContext.GetApprovers]: () => {
    return `${API_PROJECTSETUP}/approvers`;
  },
  [HttpProjectSetupContext.SyncApprovers]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/approvers`;
  },

  // Forecast categories
  [HttpProjectSetupContext.GetForecastCategories]: () => {
    return `${API_PROJECTSETUP}/forecastcategories`;
  },
  [HttpProjectSetupContext.SyncForecastCategories]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/forecastcategories`;
  },

  // Free text fields
  [HttpProjectSetupContext.GetFreeTextFields]: () => {
    return `${API_PROJECTSETUP}/freetextfields`;
  },
  [HttpProjectSetupContext.SyncFreeTextFields]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/freetextfields/`;
  },

  // Progress
  [HttpProjectSetupContext.GetProgress]: () => {
    return `${API_PROJECTSETUP}/progress`;
  },
  [HttpProjectSetupContext.SyncProgress]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/progress`;
  },

  // Progress w/o target
  [HttpProjectSetupContext.GetProgressWithoutTarget]: () => {
    return `${API_PROJECTSETUP}/progresswithouttarget`;
  },
  [HttpProjectSetupContext.SyncProgressWithoutTarget]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/progresswithouttarget`;
  },

  // Table of columns
  [HttpProjectSetupContext.GetTableOfColumns]: () => {
    return `${API_PROJECTSETUP}/tableofcolumns`;
  },
  [HttpProjectSetupContext.SyncTableOfColumns]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/tableofcolumns`;
  },

  // Hidden subtabs
  [HttpProjectSetupContext.GetHiddenSubtabs]: () => {
    return `${API_PROJECTSETUP}/hidden`;
  },
  [HttpProjectSetupContext.SyncHiddenSubtabs]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/hidden`;
  },

  // Filenames
  [HttpProjectSetupContext.GetFilenames]: () => {
    return `${API_PROJECTSETUP}/namingconvention`;
  },
  [HttpProjectSetupContext.SyncFilenames]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/namingconvention`;
  },

  // Report configuration
  [HttpProjectSetupContext.GetReportConfiguration]: () => {
    return `${API_PROJECTSETUP}/report/configuration`;
  },
  [HttpProjectSetupContext.SyncReportConfiguration]: () => {
    return `${API_PROJECTSETUP}${SYNCHRONIZE}/report/configuration`;
  },

  //#endregion Project setup

  //#region User Admin

  // Users and System Admins
  [HttpUserAdminContext.GetUsers]: () => {
    return `${API_USERSADMIN}/users`;
  },
  [HttpUserAdminContext.GetSystemAdmins]: () => {
    return `${API_USERSADMIN}/system-admins`;
  },
  [HttpUserAdminContext.SyncUsers]: () => {
    return `${API_USERSADMIN}${SYNCHRONIZE}/users`;
  },

  // User rights
  [HttpUserAdminContext.GetUserRightsSubproject]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_USERSADMIN}/subprojects/${args.accountId}/`;
  },

  //#endregion Users Admin

  //#region Project Admin

  // Projects
  [HttpProjectAdminContext.GetAllProjects]: () => {
    return `${API_SETTINGS}/projects/all`;
  },
  [HttpProjectAdminContext.GetProjects]: () => {
    return `${API_SETTINGS}/projects/`;
  },
  [HttpProjectAdminContext.SyncProjects]: () => {
    return `${API_SETTINGS}${SYNCHRONIZE}/projects/`;
  },

  // Scopes
  [HttpProjectAdminContext.GetScopes]: () => {
    return `${API_SETTINGS}/scopes/`;
  },
  [HttpProjectAdminContext.SyncScopes]: () => {
    return `${API_SETTINGS}${SYNCHRONIZE}/scopes/`;
  },

  // Vessels/Locations
  [HttpProjectAdminContext.GetVesselLocations]: () => {
    return `${API_SETTINGS}/vesselLocations/`;
  },
  [HttpProjectAdminContext.SyncVesselLocations]: () => {
    return `${API_SETTINGS}${SYNCHRONIZE}/vesselLocations/`;
  },

  // Subprojects
  [HttpProjectAdminContext.GetAllSubprojects]: () => {
    return `${API_SETTINGS}/subprojects/all`;
  },
  [HttpProjectAdminContext.GetSubprojects]: () => {
    return `${API_SETTINGS}/subprojects`;
  },
  [HttpProjectAdminContext.SyncSubprojects]: () => {
    return `${API_SETTINGS}${SYNCHRONIZE}/subprojects/`;
  },

  //#endregion Project Admin

  //#region Dprs

  [HttpDprContext.GetDprCards]: () => {
    return `${API_DPR}/getdprcards`;
  },
  [HttpDprContext.LockDpr]: () => {
    return `${API_DPR}/lock`;
  },
  [HttpDprContext.UnlockDpr]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DPR}/unlock/${args.dprDetailId}`;
  },
  [HttpDprContext.CreateDpr]: () => {
    return `${API_DPR}/create`;
  },
  [HttpDprContext.GetAvailableDates]: () => {
    return `${API_DPR}/getavailabledates`;
  },
  [HttpDprContext.GetUnlockedDprDates]: () => {
    return `${API_DPR}/getunlockeddprdates`;
  },
  [HttpDprContext.GetDprAttachmentsInfo]: () => {
    return `${API_DPR}/getdprattachmentsinfo`;
  },
  [HttpDprContext.UploadSignedDpr]: () => {
    return `${API_DPR}/uploadsigneddpr/`;
  },
  // Approvers
  [HttpDprContext.CreateDprApprover]: () => {
    return `${API_DPRAPPROVERS}/create`;
  },
  [HttpDprContext.UpdateDprApprover]: () => {
    return `${API_DPRAPPROVERS}/update`;
  },
  [HttpDprContext.DeleteDprApprover]: () => {
    return `${API_DPRAPPROVERS}/delete`;
  },
  [HttpDprContext.SyncDprOnlineApproval]: () => {
    return `${API_DPRAPPROVERS}/synchronize/dpronlineapproval`;
  },

  // Export data
  [HttpDprContext.ExportData]: () => {
    return `${API_EXPORTDATA}`;
  },

  // Digital Signature
  [HttpDprContext.GetDprSignRequestStatus]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DIGITALSIGNATURE}/status/${args.dprReportSignRequestId}`;
  },
  [HttpDprContext.SendDprSignRequest]: () => {
    return `${API_DIGITALSIGNATURE}/sendsignrequest`;
  },
  [HttpDprContext.CancelDprSignRequest]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DIGITALSIGNATURE}/cancel/${args.dprReportSignRequestId}`;
  },
  [HttpDprContext.GetPreviousDprSigners]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DIGITALSIGNATURE}/previous-signers?date=${args.utcDate}`;
  },

  //#endregion Dprs

  //#region Shared

  // Heartbeat
  [HttpSharedContext.SendHeartBeat]: () => {
    return `${API_HEARTBEAT}`;
  },

  // User Details
  [HttpSharedContext.GetUserSession]: () => {
    return `${API_USERSESSION}`;
  },
  [HttpSharedContext.GetProjectSetup]: () => {
    return `${API_PROJECTSETUP}`;
  },
  [HttpSharedContext.GetLastThreeDprs]: () => {
    return `${API_DPRDETAILS}/lastThreeDprDetails`;
  },
  [HttpSharedContext.SyncSelectedClient]: () => {
    return `${API_USERSESSION}/updateLastSelectedClientId`;
  },
  [HttpSharedContext.SyncSelectedSubproject]: () => {
    return `${API_USERSESSION}/updateLastSelectedSubprojectId`;
  },
  [HttpSharedContext.SyncUserPreferences]: () => {
    return `${API_USERSESSION}/updateUserPreferences`;
  },
  [HttpSharedContext.GetDprReportHistory]: (args: {
    [key: string]: number | string | boolean;
  }) => {
    return `${API_REPORT}/history?dprId=${args.dprId}`;
  },
  [HttpSharedContext.GetUserManual]: () => {
    return `${API_SETTINGS}/usermanualinfo/`;
  },

  // Offline Data
  [HttpSharedContext.SyncOfflineChangesExternalComments]: (args: {
    [key: string]: number | string | boolean;
  }) => {
    return `${API_DPRDETAILS}/synchronize/offlinedata/externalcomments/${args.syncOfflineGuid}`;
  },
  [HttpSharedContext.SyncOfflineChanges]: (args: {
    [key: string]: number | string | boolean;
  }) => {
    return `${API_DPRDETAILS}/synchronize/offlinedata/${args.syncOfflineGuid}`;
  },

  // Core
  [HttpSharedCoreContext.GetUser]: () => {
    return '/user';
  },
  [HttpSharedCoreContext.GetZendeskMessagingToken]: () => {
    return '/zendesk/messaging-token';
  },

  // File Upload
  [HttpSharedFileContext.UploadFile]: (args: {
    [key: string]: number | string | boolean;
  }) => {
    // eslint-disable-next-line max-len
    return `${API_FILES}/upload?directory=${args.directory}&publicallyAvailable=${args.publiclyAvailable}&generateThumbnail=${args.generateThumbnail}`;
  },
  [HttpSharedFileContext.SharedDownloadFile]: (args: {
    [key: string]: number | string | boolean;
  }) => {
    return `${API_FILES}/download?filePath=${encodeURIComponent(
      args.externalUrl
    )}&fileName=${args.fileName}`;
  },
  [HttpSharedFileContext.DownloadZipArchive]: () => {
    return `${API_FILES}/zip-archive`;
  },

  //#endregion Shared
  //#region Dpr Details

  // DPR Details Data
  [HttpDprDetailsContext.GetDprDetails]: (args) => {
    return `${API_DPRDETAILS}/${args.dprDetailId}`;
  },
  [HttpDprDetailsContext.GetDprDetailsId]: (args) => {
    return `${API_DPRDETAILS}/getdprdetailsid?dprDate=${args.utcDate}&dprId=${args.dprId}`;
  },
  [HttpDprDetailsContext.GetPreviousDprDetailsId]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DPRDETAILS}/previousId?date=${args.utcDate}`;
  },
  [HttpDprDetailsContext.GetNextDprDetailsId]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DPRDETAILS}/nextId?date=${args.utcDate}`;
  },
  [HttpDprDetailsContext.CopyDprFromPrevious]: () => {
    return `${API_DPRDETAILS}/copy/previous`;
  },
  [HttpDprDetailsContext.CopySelectedActivities]: () => {
    return `${API_DPRDETAILS}/copy/selectedactivities`;
  },
  [HttpDprDetailsContext.IsFirstDpr]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    return `${API_DPRDETAILS}/isfirstdpr?date=${args.utcDate}`;
  },

  // Synchronize
  [HttpDprDetailsContext.SyncDprActivities]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dpractivities/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprStatusTracker]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprstatustracker/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprWeather]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprweather/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprPersonnel]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprpersonnel/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprConsumable]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprconsumable/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprHse]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprhse/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprSummary]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprsummary/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprForecastActivities]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprforecastactivities/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.CopyDprForecastActivitiesFromPreviousDpr]: () => {
    return `${API_DPRDETAILS}/copy/dprforecastactivities`;
  },
  [HttpDprDetailsContext.SyncDprFreeTextField]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprfreetextfield/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprProgress]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprprogress/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprProgressWithoutTarget]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprprogresswithouttarget/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprSummaryExternalComment]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprsummary/externalcomment/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprTableOfColumns]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprtableofcolumn/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprAppendix]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprappendix/${args.isAutoSyncFromAdminChange}`;
  },
  [HttpDprDetailsContext.SyncDprHidden]: (args) => {
    return `${API_DPRDETAILS}${SYNCHRONIZE}/dprhidden/${args.isAutoSyncFromAdminChange}`;
  },

  //#endregion Dpr Details
  //#region Report

  [HttpReportContext.GetDprReportDownload]: (args: {
    [key: string]: string | number | boolean;
  }) => {
    const options = args.options ? `&${args.options}` : '';
    return `${API_REPORT}/download?dprDetailId=${args.dprDetailId}&addWatermark=${args.addWatermark}${options}`;
  },

  //#endregion Report
};
